import React, { useCallback, useEffect, useState } from 'react';
import SuperadminLayout from '../../components/SuperadminLayout';
import DateField from '../../components/DateField';
import { fetchDashboardOverview } from '../../api/dashboard';
import { todayISO } from '../../utils/date';
import './SuperadminDashboardPage.css';

const money = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const int = (n) => (n || 0).toLocaleString('en-US');

const Tile = ({ label, value, sub, tone }) => (
  <div className={`sdash-tile${tone ? ` sdash-tile-${tone}` : ''}`}>
    <span className="sdash-tile-label">{label}</span>
    <span className="sdash-tile-value">{value}</span>
    {sub && <span className="sdash-tile-sub">{sub}</span>}
  </div>
);

const TirageList = ({ title, rows }) => (
  <div className="sdash-tirage-col">
    <span className="sdash-tirage-title">{title} ({rows.length})</span>
    {rows.length === 0 && <span className="sdash-tirage-empty">—</span>}
    {rows.map((t) => (
      <span key={t.code} className="sdash-tirage-item">
        <b>{t.code}</b> {t.name} <span className="sdash-tirage-hrs">{t.start_time}–{t.end_time}</span>
      </span>
    ))}
  </div>
);

const SuperadminDashboardPage = () => {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchDashboardOverview({ date }));
    } catch (err) {
      setError('Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const m = data?.money;
  const ex = data?.exposure;

  return (
    <SuperadminLayout>
      <div className="sdash-header">
        <h2 className="sdash-title">Dashboard CGL — Vue Générale</h2>
        <div className="sdash-controls">
          <DateField value={date} onChange={setDate} />
          <button onClick={load} disabled={loading}>{loading ? '…' : 'Rafraîchir'}</button>
        </div>
      </div>

      {error && <p className="sdash-error">{error}</p>}
      {!data && loading && <p>Chargement…</p>}

      {data && (
        <>
          <h3 className="sdash-section">Structure</h3>
          <div className="sdash-grid">
            <Tile label="Total entreprises" value={int(data.companies.total)}
              sub={data.companies.blocked ? `${data.companies.blocked} bloquée(s)` : null} />
            <Tile label="Total groupes" value={int(data.groups.total)} />
            <Tile label="Total superviseurs" value={int(data.supervisors.total)}
              sub={data.supervisors.supervising_banks
                ? `${data.supervisors.supervising_banks} banque(s) superviseure(s)`
                : null} />
            <Tile label="Total POS / Agents" value={int(data.pos.total)} />
            <Tile label="POS Online" value={int(data.pos.online)} tone="ok"
              sub={`fenêtre ${data.pos.online_window_minutes} min`} />
            <Tile label="POS Offline" value={int(data.pos.offline)}
              tone={data.pos.offline ? 'warn' : null} />
          </div>

          <h3 className="sdash-section">Argent — {data.date}</h3>
          <div className="sdash-grid">
            <Tile label="Total vendu" value={money(m.sales)} sub={`${int(m.tickets)} fiche(s)`} />
            <Tile label="Total payout" value={money(m.payout)}
              sub={`${int(m.winning_tickets)} fiche(s) gagnante(s)`} />
            <Tile label="Commission" value={money(m.commission)} />
            <Tile label="Profit / Marge" value={money(m.profit)}
              tone={m.profit < 0 ? 'warn' : 'ok'} />
          </div>

          <h3 className="sdash-section">Risque (couverture)</h3>
          <div className="sdash-grid">
            <Tile label="Exposure actuel"
              value={ex.worst ? `${ex.worst.covered_pct}%` : '—'}
              tone={ex.over_limit ? 'warn' : null}
              sub={ex.worst
                ? `pire : ${ex.worst.tirage_name} / ${ex.worst.product} (limite ${ex.worst.limit_pct}%)`
                : 'aucune règle configurée'} />
            <Tile label="Règles dépassées" value={int(ex.over_limit)}
              tone={ex.over_limit ? 'warn' : 'ok'}
              sub={`${int(ex.near_limit)} proche(s) · ${int(ex.rules_configured)} règle(s)`} />
            <Tile label="Risk Capacity (cap. la + serrée)"
              value={ex.tightest_pct != null ? `${ex.tightest_pct}%` : '—'}
              sub={ex.headroom_pct != null ? `marge ${ex.headroom_pct} pts` : null} />
          </div>

          <h3 className="sdash-section">Tirages</h3>
          <div className="sdash-tirages">
            <TirageList title="En cours" rows={data.tirages.open} />
            <TirageList title="Terminés aujourd'hui" rows={data.tirages.finished_today} />
            <TirageList title="À venir aujourd'hui" rows={data.tirages.upcoming_today} />
          </div>

          <p className="sdash-generated">Généré : {new Date(data.generated_at).toLocaleString()}</p>
        </>
      )}
    </SuperadminLayout>
  );
};

export default SuperadminDashboardPage;
